import { useRef, useEffect, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { bentoItems } from "../data/portfolioData";
import { Zap, Clock, Archive, ArrowUpRight, ExternalLink } from "lucide-react";

/* ============================================================
   BEAST GRID — Phase 4: Dynamic Scrollable Showcase
   Horizontal scroll cards with animated gradient backgrounds,
   floating tech pills, parallax index numbers, and scroll-
   driven progress indicator.
   ============================================================ */

const STATUS_ICON = { LIVE: Zap, WIP: Clock, ARCHIVED: Archive };
const STATUS_STYLE = {
  LIVE: "text-primary border-primary/30 bg-primary/5",
  WIP: "text-accent border-accent/30 bg-accent/5",
  ARCHIVED: "text-muted-foreground border-border bg-muted/40",
};

/* Unique gradient backgrounds for each project — gives each card its own identity */
const CARD_GRADIENTS = [
  "radial-gradient(ellipse 80% 60% at 10% 90%, hsl(68 100% 50% / 0.06), transparent 60%), radial-gradient(ellipse 50% 50% at 90% 20%, hsl(283 55% 40% / 0.08), transparent 60%)",
  "radial-gradient(ellipse 70% 70% at 80% 80%, hsl(162 100% 50% / 0.06), transparent 55%), radial-gradient(ellipse 60% 40% at 20% 15%, hsl(283 45% 35% / 0.08), transparent 55%)",
  "radial-gradient(ellipse 90% 50% at 50% 100%, hsl(68 100% 50% / 0.05), transparent 50%), radial-gradient(ellipse 40% 60% at 85% 10%, hsl(162 60% 40% / 0.07), transparent 55%)",
  "radial-gradient(ellipse 60% 80% at 15% 50%, hsl(162 100% 50% / 0.05), transparent 55%), radial-gradient(ellipse 50% 50% at 90% 80%, hsl(68 80% 45% / 0.06), transparent 55%)",
  "radial-gradient(ellipse 80% 50% at 90% 90%, hsl(0 60% 50% / 0.04), transparent 55%), radial-gradient(ellipse 55% 55% at 10% 20%, hsl(68 100% 50% / 0.06), transparent 55%)",
  "radial-gradient(ellipse 70% 60% at 30% 85%, hsl(68 80% 45% / 0.06), transparent 55%), radial-gradient(ellipse 50% 50% at 75% 15%, hsl(162 100% 50% / 0.05), transparent 55%)",
  "radial-gradient(ellipse 100% 60% at 50% 50%, hsl(68 100% 50% / 0.04), transparent 50%), radial-gradient(ellipse 60% 50% at 10% 90%, hsl(162 80% 45% / 0.06), transparent 55%)",
];

/* ---- Animated floating particle for visual depth ---- */
const FloatingParticle = ({ delay, size, color, x, y }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      background: color,
      left: x,
      top: y,
      filter: "blur(0.5px)",
    }}
    animate={{
      y: [0, -12, 0, 8, 0],
      x: [0, 6, -4, 2, 0],
      opacity: [0.3, 0.6, 0.3, 0.5, 0.3],
    }}
    transition={{
      duration: 6 + Math.random() * 4,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

/* ---- Single Project Showcase Card ---- */
const ShowcaseCard = ({ project, index, totalCount }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-80px" });
  const isAxiom = !!project.isAxiom;
  const StatusIcon = STATUS_ICON[project.status] || Archive;
  const statusClass = STATUS_STYLE[project.status] || STATUS_STYLE.ARCHIVED;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      <a
        href={project.repoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`beast-card group relative flex flex-col overflow-hidden no-underline ${
          isAxiom ? "min-h-[460px]" : "min-h-[400px]"
        }`}
        style={{ textDecoration: "none" }}
      >
        {/* Unique gradient background for each project */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-700"
          style={{
            background: CARD_GRADIENTS[index % CARD_GRADIENTS.length],
            opacity: 0.8,
          }}
        />

        {/* Floating particles — 3 per card */}
        <FloatingParticle delay={0} size={3} color="hsl(68 100% 50% / 0.35)" x="15%" y="25%" />
        <FloatingParticle delay={1.5} size={2} color="hsl(162 100% 50% / 0.3)" x="75%" y="60%" />
        <FloatingParticle delay={3} size={2.5} color="hsl(68 100% 50% / 0.25)" x="45%" y="80%" />

        {/* Giant index number — parallax-style watermark */}
        <div
          className="absolute -right-4 -top-6 pointer-events-none select-none"
          style={{
            fontSize: "clamp(7rem, 14vw, 11rem)",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            lineHeight: 0.85,
            color: isAxiom
              ? "hsl(68 100% 50% / 0.04)"
              : "hsl(162 100% 50% / 0.04)",
          }}
        >
          {project.index}
        </div>

        {/* Top-edge hairline accent */}
        <div
          aria-hidden
          className="absolute top-0 left-[8%] right-[8%] h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: isAxiom
              ? "linear-gradient(90deg, transparent, hsl(68 100% 50% / 0.55), transparent)"
              : "linear-gradient(90deg, transparent, hsl(162 100% 50% / 0.45), transparent)",
          }}
        />

        {/* HEADER: category + status badge */}
        <div className="flex items-start justify-between gap-4 relative z-10">
          <span className="eyebrow-muted leading-tight max-w-[20ch]">
            {project.category}
          </span>
          <div
            className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border
              font-mono text-[0.6rem] tracking-[0.22em] ${statusClass}`}
          >
            <StatusIcon className="w-2.5 h-2.5" />
            {project.status}
          </div>
        </div>

        {/* TITLE */}
        <h3
          className={`font-display font-semibold tracking-tight mt-7 mb-4 text-foreground
            transition-colors duration-300 relative z-10
            ${isAxiom ? "group-hover:text-primary" : "group-hover:text-accent"}`}
          style={{
            fontSize: isAxiom
              ? "clamp(1.5rem, 2.6vw, 2rem)"
              : "clamp(1.15rem, 1.7vw, 1.35rem)",
            lineHeight: 1.15,
          }}
        >
          {project.title}
        </h3>

        {/* TAGLINE */}
        <p
          className={`text-muted-foreground leading-relaxed relative z-10 ${
            isAxiom ? "text-sm max-w-[60ch]" : "text-[0.82rem] max-w-[48ch]"
          }`}
        >
          {project.tagline}
        </p>

        {/* HAIRLINE DIVIDER */}
        <div className="h-px bg-border/80 my-6 relative z-10" />

        {/* HOVER REVEAL — Complexity Highlight */}
        <div className="beast-reveal flex-1 relative z-10">
          <div className="beast-reveal-block">
            <span className="beast-reveal-label">COMPLEXITY HIGHLIGHT</span>
            <p className="beast-reveal-text">{project.complexityHighlight}</p>
          </div>
          {isAxiom && (
            <div className="beast-reveal-block mt-5">
              <span className="beast-reveal-label">ARCHITECTURE</span>
              <p className="beast-reveal-text">{project.architecture}</p>
            </div>
          )}
        </div>

        {/* AXIOM METRICS STRIP */}
        {isAxiom && project.metrics && (
          <div className="mt-7 grid grid-cols-4 gap-3 relative z-10">
            {project.metrics.map((m) => (
              <div
                key={m.label}
                className="border border-border rounded-lg px-3 py-3 bg-black/20"
              >
                <div className="font-display font-bold text-lg text-primary leading-none mb-1.5">
                  {m.value}
                </div>
                <div className="font-mono text-[0.58rem] tracking-[0.22em] text-muted-foreground">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FOOTER — tech stack + arrow */}
        <div className="mt-auto pt-7 relative z-10">
          {/* Animated tech pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {project.tech.map((t, ti) => (
              <motion.span
                key={t}
                initial={{ opacity: 0, y: 8 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + ti * 0.05 }}
                className="px-2.5 py-1 rounded-lg text-[0.68rem] font-mono tracking-wide
                  border border-border/60 bg-black/15"
                style={{ color: "hsl(var(--accent))" }}
              >
                {t}
              </motion.span>
            ))}
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-border/60">
            <span className="font-mono text-[0.58rem] tracking-[0.28em] text-muted-foreground uppercase">
              view on github
            </span>
            <ArrowUpRight
              className={`w-4 h-4 transition-all duration-300 opacity-40
                group-hover:opacity-100 translate-x-[-4px] translate-y-[4px]
                group-hover:translate-x-0 group-hover:translate-y-0
                ${isAxiom ? "text-primary" : "text-accent"}`}
            />
          </div>
        </div>
      </a>
    </motion.div>
  );
};

/* ---- Scroll Progress Bar ---- */
const ScrollProgress = ({ scrollRef }) => {
  const { scrollXProgress } = useScroll({ container: scrollRef });
  const width = useTransform(scrollXProgress, [0, 1], ["0%", "100%"]);

  return (
    <div className="h-0.5 bg-border/40 rounded-full overflow-hidden mt-8">
      <motion.div
        className="h-full rounded-full"
        style={{
          width,
          background: "linear-gradient(90deg, hsl(68 100% 50%), hsl(162 100% 50%))",
        }}
      />
    </div>
  );
};

/* ---- Main Grid Section ---- */
export const BeastGrid = () => {
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: true });

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 10,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 10,
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener("scroll", checkScroll);
  }, []);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector(".beast-card")?.offsetWidth || 420;
    el.scrollBy({ left: dir * (cardWidth + 16), behavior: "smooth" });
  };

  return (
    <section id="projects" className="relative pt-32 pb-16 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* SECTION HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65 }}
          className="mb-12"
          data-testid="beast-grid-header"
        >
          <div className="eyebrow mb-5">// the_beast_projects</div>
          <div className="flex items-end justify-between gap-10 flex-wrap">
            <h2
              className="font-display font-bold text-foreground leading-[1.02]"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 4rem)" }}
            >
              Systems that{" "}
              <span className="text-gradient-wasabi-mint">refuse to fail</span>.
            </h2>
            <div className="flex items-end gap-5 pb-2 flex-1 min-w-[200px]">
              <div className="flex-1 h-px bg-border" />
              <span
                className="font-mono text-xs whitespace-nowrap"
                style={{ color: "hsl(var(--foreground-subtle))" }}
              >
                07 deployed
              </span>
            </div>
          </div>
          <p className="mt-6 text-muted-foreground max-w-[62ch] text-sm leading-relaxed">
            Production-grade architectures — edge AI, agentic LLM systems,
            privacy-first mobile, and the commercial web. Hover any card to
            reveal its complexity signature.
          </p>
        </motion.div>

        {/* Scroll navigation arrows */}
        <div className="flex items-center justify-end gap-3 mb-6">
          <span className="font-mono text-xs text-muted-foreground tracking-wider mr-auto">
            SCROLL →
          </span>
          <button
            onClick={() => scroll(-1)}
            disabled={!canScroll.left}
            className={`w-10 h-10 rounded-xl glass-card flex items-center justify-center font-mono text-sm transition-all
              ${canScroll.left ? "text-foreground hover:border-primary/40" : "text-muted-foreground/30 cursor-default"}`}
          >
            ←
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={!canScroll.right}
            className={`w-10 h-10 rounded-xl glass-card flex items-center justify-center font-mono text-sm transition-all
              ${canScroll.right ? "text-foreground hover:border-primary/40" : "text-muted-foreground/30 cursor-default"}`}
          >
            →
          </button>
        </div>

        {/* HORIZONTAL SCROLL SHOWCASE */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 hide-scrollbar"
          style={{ scrollbarWidth: "none" }}
          data-testid="beast-grid"
        >
          {bentoItems.map((item, i) => (
            <div
              key={item.id}
              className={`flex-shrink-0 ${
                item.isAxiom ? "w-[85vw] sm:w-[520px] md:w-[600px]" : "w-[85vw] sm:w-[380px] md:w-[420px]"
              }`}
            >
              <ShowcaseCard
                project={item}
                index={i}
                totalCount={bentoItems.length}
              />
            </div>
          ))}
        </div>

        {/* Scroll progress indicator */}
        <ScrollProgress scrollRef={scrollRef} />
      </div>
    </section>
  );
};

export default BeastGrid;
